import React, { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { DateTime } from 'luxon'
import PropTypes from 'prop-types'

// material-ui
import { useTheme } from '@mui/material/styles'
import { Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Stack, TextField, Typography } from '@mui/material'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import ArchiveOutlined from '@mui/icons-material/ArchiveOutlined'

// project imports
import useCurrentUser from 'hooks/useCurrentUser'
import useMarkets from 'hooks/useMarkets'
import useProject, { useProjectForm } from 'hooks/useProject'

import ModalContext from 'contexts/modalContext'
import ProjectArchived from 'layout/ProjectArchived'
import { getApiErrorMessage } from 'shared/functions/errors'

import { CustomIcon, ModalContainer } from '@contenthouse/components'
import { useSetState } from '@contenthouse/hooks'
import { digObject } from '@contenthouse/utilities'

//state
const defaultState = {
  errors: [],
  openArchive: false,
  ownerUser: null,
}

const ProjectModal = ({ callbacks, modalKey = 'ProjectModal', showModal }) => {
  const { t } = useTranslation('modals')
  const navigate = useNavigate()

  const [state, setState] = useSetState(defaultState)
  const { errors, openArchive, ownerUser } = state

  const theme = useTheme()

  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, `${modalKey}`, {})

  const { project } = modalPayload

  const { users } = useSelector((reduxState) => reduxState.entities)

  const projectFormPayload = useProjectForm(project)
  const {
    entityState,
    entityState: { display_name, owner, content_express_agency_name },
    saveEnabled,
    setEntityState,
  } = projectFormPayload

  const {
    callbacks: { createProject: createFn, patchProject: patchFn },
    creating,
    updating,
  } = useProject()

  const { currentUser } = useCurrentUser()
  const { defaultOffice } = useMarkets()

  const office = currentUser?.office || entityState.office || defaultOffice

  function getDifference(reference, newValues) {
    if (typeof reference !== 'object' || reference === null || typeof newValues !== 'object' || newValues === null) {
      throw new TypeError('Both inputs must be non-null objects.')
    }

    const differences = {}

    for (const key in newValues) {
      if (newValues.hasOwnProperty(key)) {
        if (!reference.hasOwnProperty(key) || reference[key] !== newValues[key]) {
          differences[key] = newValues[key]
        }
      }
    }

    return differences
  }

  const save = (id) => {
    if (id) {
      const updatedValues = getDifference(project, entityState)
      patchFn({ id, ...updatedValues }).then(({ success, errors, result }) => {
        if (!success && errors) {
          toast.warn(getApiErrorMessage(errors))
          return
        }
        closeModal()
      })
    } else {
      createFn({ ...entityState, office }).then(({ success, errors, result }) => {
        if (!success && errors) {
          toast.warn(getApiErrorMessage(errors))
          return
        }
        closeModal()
        navigate(`/project/${result.id}`, {
          state: {
            category: project ? project?.category?.icon : '',
          },
        })
      })
    }
  }

  const archive = () => {
    patchFn({ id: project.id, archive_date: DateTime.now().toUTC().toISO() }).then(({ success, errors }) => {
      if (!success && errors) {
        toast.warn(getApiErrorMessage(errors))
        return
      }
      closeModal()
      navigate('/')
    })
  }

  const setArchive = (val) => {
    setState({ openArchive: val })
  }

  useEffect(() => {
    setEntityState({ owner: project?.id ? project?.owner : currentUser.id })
  }, [project?.id])

  useEffect(() => {
    if (users[owner] && users[owner].id !== ownerUser?.id) {
      setState({ ownerUser: users[owner] })
    }
  }, [users, owner])

  return (
    <ModalContainer callbacks={callbacks} modalKey={modalKey} showModal={showModal} size={project?.category || project?.archive_date ? '' : 'large'}>
      <ModalContainer.Header
        callbacks={callbacks}
        heading={project?.id ? t('project.editHeading') : project?.category ? t('project.quickOrderHeading') : t('project.newHeading')}
        headingNode={
          !project?.id && project?.category ? (
            <Chip
              icon={<CustomIcon name={project?.category?.icon} width={23} height={23} marginY={2} marginX={1} />}
              label={project?.category?.name}
              sx={{ mt: 1, '& .MuiChip-label': { pl: 0 } }}
            />
          ) : null
        }
      />
      <ModalContainer.Content>
        {project?.archive_date ? (
          <ProjectArchived project={project} callbacks={{ toggleUnarchiveSuccess: () => closeModal() }} />
        ) : (
          <Stack direction="column" p={2} spacing={2}>
            <TextField
              id="outlined-basic-name"
              label={t('project.projectName')}
              required
              value={display_name}
              variant="outlined"
              fullWidth
              onChange={(e) => {
                setEntityState({ display_name: e.target.value })
              }}
            />
            <TextField
              id="outlined-basic-owner"
              label={t('project.owner')}
              disabled
              value={ownerUser?.name || t('project.noOwner')}
              variant="outlined"
              fullWidth
            />
            <Stack>
              <TextField
                id="outlined-basic-agency"
                label={t('project.agencyName')}
                required
                value={content_express_agency_name}
                variant="outlined"
                fullWidth
                onChange={(e) => {
                  setEntityState({ content_express_agency_name: e.target.value })
                }}
              />
              <Typography variant="subtitle2" sx={{ mt: 0.5, ml: 1 }}>
                {t('project.agencyHint')}
              </Typography>
            </Stack>
            {entityState?.id && (
              <Stack direction="column" spacing={2}>
                <Typography variant="h5">{t('project.otherOptions')}</Typography>
                <Button
                  loading={updating}
                  disabled={!entityState?.id}
                  variant="contained"
                  disableElevation
                  fullWidth
                  onClick={() => setArchive(true)}
                >
                  {t('project.archive')}
                </Button>
              </Stack>
            )}
          </Stack>
        )}
        <Dialog
          open={openArchive}
          onClose={() => setArchive(false)}
          sx={{
            '& .MuiDialogTitle-root': { fontSize: '1.25rem!important' },
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle sx={{ borderBottom: 1, marginBottom: 1, borderColor: theme.palette.primary.light }}>
            <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
              <Typography variant="h4" sx={{ fontWeight: 500 }}>
                {t('project.archiveConfirm.title')}
              </Typography>
              <CloseOutlinedIcon fontSize="small" sx={{ cursor: 'pointer' }} onClick={() => setArchive(false)} />
            </Stack>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <ArchiveOutlined sx={{ fontSize: 80 }} />
            <Typography variant="h4" sx={{ py: 1 }}>
              {t('project.archiveConfirm.areYouSure')}
            </Typography>
            <DialogContentText>{t('project.archiveConfirm.body1')}</DialogContentText>
            <DialogContentText>{t('project.archiveConfirm.body2')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setArchive(false)}>{t('project.archiveConfirm.cancel')}</Button>
            <Button loading={updating} variant="contained" disableElevation onClick={() => archive()}>
              {t('project.archiveConfirm.confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      </ModalContainer.Content>
      <ModalContainer.Footer>
        {project?.archive_date ? null : (
          <Button
            disableElevation
            disabled={!saveEnabled || !!errors.length || (!project?.id && !office)}
            loading={creating || updating}
            fullWidth
            onClick={() => save(project?.id)}
            variant="contained"
          >
            {project?.id ? t('project.save') : t('project.create')}
          </Button>
        )}
        <Button onClick={closeModal} variant="outlined" fullWidth style={{ marginTop: 6 }}>
          {t('project.cancel')}
        </Button>
      </ModalContainer.Footer>
    </ModalContainer>
  )
}

ProjectModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = (props) => (
  <ModalContainer.RenderController {...props}>
    <ProjectModal {...props} />
  </ModalContainer.RenderController>
)

export default LazyLoadedModal
